import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBoxesStacked,
  faEnvelope,
  faTriangleExclamation,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons';
import { reportKeys, reportsApi } from '../features/reports/reportsApi';
import { MOVEMENT_TYPE_LABELS } from '../features/inventory/labels';
import { ORDER_STATUS_LABELS } from '../features/orders/labels';
import { useAuth } from '../features/auth/useAuth';
import { isApiError } from '../lib/apiError';
import { formatQuantity } from '../lib/quantity';
import { ROUTES } from '../routes/paths';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/PageState';
import type { Dashboard } from '../types/api';

interface MetricProps {
  label: string;
  value: number;
  icon: IconDefinition;
  to: string;
  /** Un cero no es una alerta: es la respuesta buena. */
  isAlert: boolean;
  hint: string;
}

/**
 * Tarjeta de un indicador.
 *
 * Siempre lleva a la pantalla donde se resuelve. Un número que no se puede
 * abrir obliga a buscar a mano lo que el panel acaba de señalar, que es
 * exactamente el trabajo que el panel existía para ahorrar.
 */
function Metric({ label, value, icon, to, isAlert, hint }: MetricProps): ReactNode {
  const alert = isAlert && value > 0;

  return (
    <Link
      to={to}
      className="block rounded-card border border-line bg-surface p-4 hover:border-primary"
    >
      <span className="flex items-center justify-between">
        <span className="text-sm text-muted">{label}</span>
        <FontAwesomeIcon
          icon={icon}
          aria-hidden="true"
          className={alert ? 'text-danger' : 'text-muted'}
        />
      </span>
      <span
        className={`mt-2 block text-3xl font-semibold tabular-nums ${
          alert ? 'text-danger' : 'text-content'
        }`}
      >
        {value}
      </span>
      <span className="mt-1 block text-xs text-muted">{hint}</span>
    </Link>
  );
}

function formatWhen(value: string): string {
  return new Date(value).toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function LowStockBlock({ data }: { data: Dashboard }): ReactNode {
  return (
    <section className="rounded-card border border-line bg-surface">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold text-content">Stock bajo</h2>
        <Link
          to={ROUTES.warehouse.lowStock}
          className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
        >
          Ver todo
        </Link>
      </div>

      {data.low_stock.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">
          Ningun producto por debajo de su umbral. Los umbrales se configuran en Stock por
          ubicación.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {data.low_stock.map((row) => (
            <li
              key={`${row.product_id}-${row.location_id}`}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-content">
                  {row.product_name}
                </span>
                <span className="block text-xs text-muted">
                  {row.product_sku} · {row.location_name}
                </span>
              </span>
              <span className="text-right">
                <span className="block text-sm tabular-nums text-danger">
                  Faltan {formatQuantity(row.missing_quantity)} {row.base_unit_name ?? ''}
                </span>
                <span className="block text-xs text-muted">
                  Disponible {formatQuantity(row.available_quantity)} de{' '}
                  {formatQuantity(row.min_quantity)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MovementsBlock({ data }: { data: Dashboard }): ReactNode {
  return (
    <section className="rounded-card border border-line bg-surface">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold text-content">Últimos movimientos</h2>
        <Link
          to={ROUTES.warehouse.kardex}
          className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
        >
          Ver el Kardex
        </Link>
      </div>

      {data.recent_movements.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">Todavía no hay movimientos de inventario.</p>
      ) : (
        <ul className="divide-y divide-line">
          {data.recent_movements.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-content">{row.product_name}</span>
                <span className="block text-xs text-muted">
                  {MOVEMENT_TYPE_LABELS[row.movement_type]} ·{' '}
                  {row.destination_location_name ?? row.source_location_name ?? 'Sin ubicación'}
                </span>
              </span>
              <span className="text-right">
                <span className="block text-sm tabular-nums text-content">
                  {formatQuantity(row.quantity)}
                </span>
                <span className="block text-xs text-muted">{formatWhen(row.created_at)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LocationsBlock({ data }: { data: Dashboard }): ReactNode {
  return (
    <section className="rounded-card border border-line bg-surface">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold text-content">Material por ubicación</h2>
        <Link
          to={ROUTES.warehouse.stock}
          className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
        >
          Ver el stock
        </Link>
      </div>

      {data.stock_by_location.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">No hay existencias en ninguna ubicación.</p>
      ) : (
        <ul className="divide-y divide-line">
          {data.stock_by_location.map((row) => (
            <li
              key={row.location_id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-content">{row.location_name}</span>
                <span className="block text-xs text-muted">
                  {row.product_count} {row.product_count === 1 ? 'referencia' : 'referencias'}
                </span>
              </span>
              <span className="text-sm tabular-nums text-content">
                {formatQuantity(row.physical_quantity)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Panel administrativo.
 *
 * Responde una pregunta: qué necesita atención hoy. Por eso los cuatro números
 * de arriba son cosas que alguien tiene que resolver —material por debajo del
 * umbral, incidentes abiertos, comprobantes sin entregar, órdenes en marcha— y
 * no métricas de volumen. Un panel lleno de gráficas que nadie usa para decidir
 * nada es peor que no tener panel, porque enseña a ignorarlo.
 */
export function DashboardPage(): ReactNode {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: reportKeys.dashboard,
    queryFn: () => reportsApi.dashboard(),
  });

  if (query.isPending) {
    return <LoadingState label="Cargando el panel" />;
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        message={isApiError(query.error) ? query.error.message : 'No se pudo cargar el panel.'}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data;
  const nothingYet =
    data.recent_movements.length === 0 &&
    data.stock_by_location.length === 0 &&
    data.active_order_count === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Panel"
        description={`Hola, ${user?.full_name ?? ''}. Esto es lo que necesita atención.`}
      />

      {nothingYet ? (
        <EmptyState
          title="Todavía no hay operación registrada"
          message="Registra una entrada de inventario para empezar a ver movimientos y existencias."
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label="Stock bajo"
          value={data.low_stock_count}
          icon={faBoxesStacked}
          to={ROUTES.warehouse.lowStock}
          isAlert
          hint="Productos por debajo de su umbral"
        />
        <Metric
          label="Incidentes abiertos"
          value={data.open_incident_count}
          icon={faTriangleExclamation}
          to={ROUTES.warehouse.incidents}
          isAlert
          hint="Reportados en ruta y sin resolver"
        />
        <Metric
          label="Comprobantes sin entregar"
          value={data.failed_email_count}
          icon={faEnvelope}
          to={ROUTES.warehouse.emails}
          isAlert
          hint="Correos fallidos, reintentables"
        />
        <Metric
          label="Órdenes en marcha"
          value={data.active_order_count}
          icon={faTruckFast}
          to={ROUTES.warehouse.orders}
          isAlert={false}
          hint="Confirmadas, cargando o en ruta"
        />
      </div>

      {data.orders_by_status.length > 0 ? (
        <section className="rounded-card border border-line bg-surface p-4">
          <h2 className="text-sm font-semibold text-content">Órdenes por estado</h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.orders_by_status.map((row) => (
              <li key={row.status}>
                <Badge tone="warning">
                  {ORDER_STATUS_LABELS[row.status]}: {row.count}
                </Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <LowStockBlock data={data} />
        <MovementsBlock data={data} />
      </div>

      <LocationsBlock data={data} />
    </div>
  );
}
